<template>
  <div>
    <template v-if="subscriptionStatus === false && isExpired !== true && hasActivePaymentMethod">
      <div class="text-center">
        <AppDialog
          v-model="dialogActive"
          width="1178"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              v-bind="attrs"
              v-on="on"
              class="dashboard-payment-btn"
            >
              {{ $t('renewSubscription') }}
            </button>
          </template>

          <div class="subscription-dialog">
            <div class="subscription-dialog__title">
              {{ $t('subscriptionRenewDialogTitle') }}
            </div>
            <div class="subscription-dialog__subtitle">
              {{ $t('subscriptionRenewDialogSubtitle') }}
              <span class="subscription-dialog__date">{{ expirationText }},</span>
              {{ $t('subscriptionRenewDialogSubtitle2') }}
            </div>
            <div class="subscription-dialog__text">
              {{ $t('subscriptionRenewDialogText', { date: nextPaymentText }) }}
            </div>
            <div class="subscription-dialog__buttons">
              <button
                class="subscription-dialog__btn"
                @click="dialogActive = false"
              >
                {{ $t('cancel') }}
              </button>
              <button
                class="subscription-dialog__btn subscription-dialog__btn_activate"
                @click="dialogActive = renewSubscriptionDialog()"
              >
                {{ $t('renewSubscription') }}
              </button>
            </div>
          </div>
        </AppDialog>
      </div>
    </template>
    <template v-if="subscriptionStatus === false && isExpired !== true && hasActivePaymentMethod !== true">
      <div class="text-center">
        <AppDialog
          v-model="dialogActiveWithoutPaymentMethod"
          width="1178"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              v-bind="attrs"
              v-on="on"
              class="dashboard-payment-btn"
            >
              {{ $t('renewSubscription') }}
            </button>
          </template>

          <div class="subscription-dialog">
            <div class="subscription-dialog__title">
              {{ $t('subscriptionRenewDialogTitle') }}
            </div>
            <div class="subscription-dialog__subtitle">
              {{ $t('subscriptionRenewDialogEmptyPayment', { date: nextPaymentText }) }}
            </div>
            <div class="subscription-dialog__buttons">
              <button
                @click="dialogActiveWithoutPaymentMethod = false"
                class="subscription-dialog__btn"
              >
                {{ $t('cancel') }}
              </button>
              <button
                @click="dialogActiveWithoutPaymentMethod = addPaymentMethodInfo()"
                class="subscription-dialog__btn subscription-dialog__btn_activate"
              >
                {{ $t('renewSubscription') }}
              </button>
            </div>
          </div>
        </AppDialog>
      </div>
    </template>
    <template v-if="isExpired === true">
      <button @click="changePlan()" class="dashboard-payment-btn">
        {{ $t('renewSubscription') }}
      </button>
    </template>
  </div>
</template>

<script>
import useSubscription from '@/compositions/accounting/useSubscription';
import { paymentTabs, settingsTabs } from '@/enums';
import useAccount from '@/compositions/accounting/useAccount';
import AppDialog from '@/components/AppDialog';
import { ref } from '@vue/composition-api';

export default {
  name: 'RenewSubscription',
  components: { AppDialog },
  setup(_, { root: { $router } }) {
    const {
      subscriptionStatus, renewActiveSubscription, isExpired,
    } = useSubscription();
    const {
      nextPaymentText, hasActivePaymentMethod, expirationText,
    } = useAccount();

    const dialogActive = ref(false);
    const dialogActiveWithoutPaymentMethod = ref(false);

    function changePlan() {
      $router.push({ name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS } });
    }

    function addPaymentMethodInfo() {
      $router.push({ name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.INFO }, query: { renew: true } });
      return false;
    }

    function renewSubscriptionDialog() {
      renewActiveSubscription();
      return false;
    }

    return {
      isExpired,
      subscriptionStatus,
      renewSubscriptionDialog,
      changePlan,
      nextPaymentText,
      hasActivePaymentMethod,
      addPaymentMethodInfo,
      expirationText,
      dialogActive,
      dialogActiveWithoutPaymentMethod,
    };
  },
};
</script>

<style lang="scss" scoped>
  .dashboard-payment-btn {
    display: block;
    font-size: 0.875rem;
    padding: 0.4375rem 1.125rem;
    border: 1px solid #D6D6D6;
    border-radius: 0.25rem;
    margin-left: auto;
    margin-bottom: 1.25rem;
  }
  .subscription-dialog {
    padding: 1.875rem 1.875rem 3.125rem;
    background: #FFFFFF;
    font-weight: 400;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.04);
    border-radius: 0.625rem;
    &__title {
      font-size: 2.125rem;
      line-height: 1.32;
      text-align: center;
      color: #091A35;
      margin-bottom: 0.625rem;
    }
    &__subtitle {
      max-width: 28rem;
      font-size: 1.0625rem;
      line-height: 1.53;
      text-align: center;
      color: #091A35;
      margin: 0 auto 1.25rem;
    }
    &__text {
      max-width: 30rem;
      color: #091A35;
      font-size: 0.9375rem;
      line-height: 1.33;
      margin: 0 auto 1.375rem;
    }
    &__text-items {
      margin-bottom: 2.5rem;
    }
    &__text-item {
      width: 30rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      color: #091A35;
      line-height: 1.375;
      margin: 0 auto 0.625rem auto;
      overflow: hidden;
      &:after {
        content: '';
        background: url("../../../assets/img/account/plan/dashboard-details-bg.svg") center;
        width: 100%;
        height: 1px;
        position: absolute;
        left: 0;
        bottom: 0.375rem;
        z-index: 1;
      }
    }
    &__text-caption, &__text-value {
      background: #FFFFFF;
      position: relative;
      z-index: 2;
    }
    &__buttons {
      text-align: center;
      margin-top: 2.5rem;
    }
    &__btn {
      display: inline-block;
      padding: 1rem 1.25rem;
      border: 1px solid #D6D6D6;
      line-height: 1.15;
      font-weight: 400;
      letter-spacing: 0.02em;
      border-radius: 0.25rem;
      margin-right: 1.25rem;
      &_activate {
        color: #FFFFFF;
        border-color: #1FC57F;
        background: #1FC57F;
        box-shadow: 0 2px 0.25rem rgba(0, 0, 0, 0.24);
      }
      &:last-child {
        margin-right: 0;
      }
    }
  }

  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .dashboard-payment-btn {
      margin: 0 auto;
    }
    .subscription-dialog {
      padding: 1.875rem 1.125rem;
      &__title {
        font-size: 1.5rem;
        font-weight: 500;
        line-height: 1.1;
        margin-bottom: 1.125rem;
      }
      &__text-items {
        margin-bottom: 1.5rem;
      }
      &__text-item {
        max-width: 25rem;
        font-size: 0.9375rem;
      }
      &__buttons {
        margin-top: 1.75rem;
      }
      &__btn {
        padding: 0.875rem 1.25rem;
      }
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .dashboard-payment-btn {
      font-size: 0.8125rem;
    }
    .subscription-dialog {
      padding: 1.875rem 1.125rem 1.625rem;
      &__title {
        font-size: 1.375rem;
        font-weight: 500;
        line-height: 1.1;
        margin-bottom: 1rem;
      }
      &__subtitle {
        max-width: 16rem;
        text-align: left;
        font-size: 1rem;
        line-height: 1.3;
        margin: 0 auto 1rem;
      }
      &__date {
        display: block;
      }
      &__text {
        font-size: 0.875rem;
        line-height: 1.5;
        margin: 0 auto 1.25rem;
      }
      &__text-items {
        margin-bottom: 1.25rem;
      }
      &__text-item {
        max-width: 20rem;
        width: 100%;
        font-size: 0.8125rem;
      }
      &__buttons {
        margin-top: 1.25rem;
      }
      &__btn {
        display: block;
        margin: 0 auto 1.25rem;
        &:last-child {
          margin-right: auto;
          margin-bottom: 0;
        }
      }
    }
  }
</style>

<i18n lang="yml" locale="ru">
cancel: Отмена
renewSubscription: Возобновить подписку
subscriptionRenewDialogTitle: Возобновление подписки
subscriptionRenewDialogEmptyPayment: Для возобновления подписки необходимо выбрать способ платежа.
subscriptionRenewDialogSubtitle: Ваша подписка оплачена до
subscriptionRenewDialogSubtitle2: но автоматическое продление сейчас отключено, и счёт за следующий период не будет выставлен.
subscriptionRenewDialogText: 'Если вы активируете подписку сегодня, то {date} произойдёт списание с карты за следующий период, после чего продление будет производиться на регулярной основе в соответствии с выбранным тарифным планом. Если вы решите отказаться от использования услуг, отмену подписки необходимо выполнить как минимум за 1 день до окончания оплаченного периода, в противном случае с вашей карты будут списаны деньги за следующий период.'
</i18n>
<i18n lang="yml" locale="en">
cancel: Cancel
renewSubscription: Renew subscription
subscriptionRenewDialogTitle: Renew subscription
subscriptionRenewDialogEmptyPayment: You should choose payment method before subscription renewal.
subscriptionRenewDialogSubtitle: Your subscription has been paid until
subscriptionRenewDialogSubtitle2: but automatic renewal is now disabled.
subscriptionRenewDialogText: 'If you activate the subscription, on {date} your card will be charged for the next period, after which the renewal will be carried out on a regular basis in accordance with the selected plan. If you decide to stop using our service, you must cancel your subscription at least 1 day before the end of the paid period, otherwise your card will be charged for the next period.'
</i18n>
